import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';

// --- Types ---

interface PropertyAnalytics {
  id: number;
  title: string;
  views: number;
  favorites: number;
  inquiries: number;
}

interface PropertiesAnalyticsResponse {
  data: PropertyAnalytics[];
}

interface VisitStatusCount {
  status: string;
  count: number;
}

interface MonthlyVisitCount {
  month: number;
  year: number;
  count: number;
}

interface VisitsAnalyticsResponse {
  data: {
    by_status: Record<string, number>;
    by_month: MonthlyVisitCount[];
    total: number;
  };
}

interface ContractsAnalyticsResponse {
  data: {
    by_type: Record<string, number>;
    by_status: Record<string, number>;
    monthly_rental_revenue: number;
    total: number;
  };
}

// --- Constants ---

const STATUS_COLORS: Record<string, string> = {
  requested: 'bg-warning',
  confirmed: 'bg-info',
  completed: 'bg-success',
  cancelled: 'bg-error',
  postponed: 'bg-neutral',
  no_show: 'bg-error/50',
};

const STATUS_LABEL_ES: Record<string, string> = {
  requested: 'Solicitada',
  confirmed: 'Confirmada',
  completed: 'Completada',
  cancelled: 'Cancelada',
  postponed: 'Postergada',
  no_show: 'No asistio',
};

const CONTRACT_TYPE_LABEL: Record<string, string> = {
  rent: 'Alquiler',
  sale: 'Venta',
  temporary: 'Temporario',
};

// --- Component ---

export default function AnalyticsPage(): React.ReactElement {
  // --- Queries ---

  const { data: propertiesData, isLoading: loadingProperties } = useQuery({
    queryKey: ['analytics-properties'],
    queryFn: () => api.get<PropertiesAnalyticsResponse>('/v1/analytics/properties'),
  });

  const { data: visitsData, isLoading: loadingVisits } = useQuery({
    queryKey: ['analytics-visits'],
    queryFn: () => api.get<VisitsAnalyticsResponse>('/v1/analytics/visits'),
  });

  const { data: contractsData, isLoading: loadingContracts } = useQuery({
    queryKey: ['analytics-contracts'],
    queryFn: () => api.get<ContractsAnalyticsResponse>('/v1/analytics/contracts'),
  });

  const properties = propertiesData?.data ?? [];
  const propertyTotals = properties.reduce(
    (acc, p) => ({ views: acc.views + p.views, favorites: acc.favorites + p.favorites, inquiries: acc.inquiries + p.inquiries }),
    { views: 0, favorites: 0, inquiries: 0 }
  );
  const visitsByStatusRaw = visitsData?.data?.by_status ?? {};
  const visitsByStatus: VisitStatusCount[] = Object.entries(visitsByStatusRaw).map(([status, count]) => ({ status, count }));
  const MONTH_NAMES: Record<number, string> = { 1: 'Ene', 2: 'Feb', 3: 'Mar', 4: 'Abr', 5: 'May', 6: 'Jun', 7: 'Jul', 8: 'Ago', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dic' };
  const visitsMonthly = (visitsData?.data?.by_month ?? []).map((m) => ({ ...m, label: `${MONTH_NAMES[m.month] ?? m.month} ${m.year}` }));
  const totalVisits = visitsData?.data?.total ?? 0;
  const contractsByTypeRaw = contractsData?.data?.by_type ?? {};
  const contractsByType = Object.entries(contractsByTypeRaw).map(([type, count]) => ({ type, count }));
  const totalRevenue = contractsData?.data?.monthly_rental_revenue ?? 0;
  const activeContracts = contractsData?.data?.total ?? 0;

  const isLoading = loadingProperties || loadingVisits || loadingContracts;

  // Max values for bar charts
  const maxPropertyViews = Math.max(...properties.map((p) => p.views), 1);
  const maxMonthlyVisits = Math.max(...visitsMonthly.map((m) => m.count), 1);
  const totalVisitsByStatus = visitsByStatus.reduce((sum, s) => sum + s.count, 0) || 1;

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value);
  };

  // --- Render ---

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Analiticas</h1>
        <p className="text-sm text-base-content/60">
          Metricas de rendimiento de tu empresa
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="stat bg-base-100 rounded-box shadow-sm p-4">
          <div className="stat-title text-xs">Total vistas</div>
          <div className="stat-value text-2xl">{propertyTotals.views.toLocaleString('es-AR')}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow-sm p-4">
          <div className="stat-title text-xs">Total favoritos</div>
          <div className="stat-value text-2xl">{propertyTotals.favorites.toLocaleString('es-AR')}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow-sm p-4">
          <div className="stat-title text-xs">Total consultas</div>
          <div className="stat-value text-2xl">{propertyTotals.inquiries.toLocaleString('es-AR')}</div>
        </div>
        <div className="stat bg-base-100 rounded-box shadow-sm p-4">
          <div className="stat-title text-xs">Total visitas</div>
          <div className="stat-value text-2xl">{totalVisits.toLocaleString('es-AR')}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Properties: Top by Views */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Propiedades - Top por vistas</h2>
            {properties.length === 0 ? (
              <p className="text-base-content/50 text-sm py-4">Sin datos disponibles</p>
            ) : (
              <div className="space-y-3 mt-2">
                {properties.slice(0, 10).map((prop) => (
                  <div key={prop.id}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium truncate max-w-[60%]">{prop.title}</span>
                      <span className="text-sm text-base-content/60">{prop.views} vistas</span>
                    </div>
                    <div className="w-full bg-base-200 rounded-full h-2.5">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all"
                        style={{ width: `${(prop.views / maxPropertyViews) * 100}%` }}
                      />
                    </div>
                    <div className="flex gap-4 mt-1">
                      <span className="text-xs text-base-content/50">
                        &#10084; {prop.favorites} favoritos
                      </span>
                      <span className="text-xs text-base-content/50">
                        &#128172; {prop.inquiries} consultas
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Visits: Status Distribution */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Visitas - Distribucion por estado</h2>
            {visitsByStatus.length === 0 ? (
              <p className="text-base-content/50 text-sm py-4">Sin datos disponibles</p>
            ) : (
              <div className="space-y-3 mt-2">
                {visitsByStatus.map((item) => {
                  const pct = Math.round((item.count / totalVisitsByStatus) * 100);
                  return (
                    <div key={item.status} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${STATUS_COLORS[item.status] || 'bg-neutral'}`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">
                            {STATUS_LABEL_ES[item.status] || item.status}
                          </span>
                          <span className="text-sm text-base-content/60">
                            {item.count} ({pct}%)
                          </span>
                        </div>
                        <div className="w-full bg-base-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${STATUS_COLORS[item.status] || 'bg-neutral'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Visits: Monthly Trend */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Visitas - Tendencia mensual</h2>
            {visitsMonthly.length === 0 ? (
              <p className="text-base-content/50 text-sm py-4">Sin datos disponibles</p>
            ) : (
              <div className="flex items-end gap-2 mt-4 h-40">
                {visitsMonthly.map((month) => {
                  const heightPct = (month.count / maxMonthlyVisits) * 100;
                  return (
                    <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-base-content/60">{month.count}</span>
                      <div className="w-full flex justify-center" style={{ height: '120px' }}>
                        <div
                          className="w-full max-w-[32px] bg-secondary rounded-t transition-all"
                          style={{ height: `${Math.max(heightPct, 4)}%`, marginTop: 'auto' }}
                        />
                      </div>
                      <span className="text-xs text-base-content/50 truncate w-full text-center">
                        {month.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Contracts */}
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-lg">Contratos</h2>

            {/* Revenue + active stats */}
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="bg-base-200 rounded-lg p-3">
                <p className="text-xs text-base-content/60">Ingresos activos</p>
                <p className="text-lg font-bold">{formatCurrency(totalRevenue)}</p>
              </div>
              <div className="bg-base-200 rounded-lg p-3">
                <p className="text-xs text-base-content/60">Contratos activos</p>
                <p className="text-lg font-bold">{activeContracts}</p>
              </div>
            </div>

            {/* Type distribution */}
            <h3 className="text-sm font-semibold mt-4 mb-2">Distribucion por tipo</h3>
            {contractsByType.length === 0 ? (
              <p className="text-base-content/50 text-sm">Sin datos disponibles</p>
            ) : (
              <div className="space-y-2">
                {contractsByType.map((item) => (
                  <div key={item.type} className="flex items-center justify-between">
                    <span className="text-sm">{CONTRACT_TYPE_LABEL[item.type] || item.type}</span>
                    <span className="badge badge-sm badge-outline">{item.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
